import { DecayFunction } from "./decay-time";
import {
  DIMENSION,
  RoomSingleFreq,
  SixDirectional,
  TriDimensional
} from "./room";

/**
 * Total area weighted average absorption factor of the room.
 * ̅α or αₛ from (Zhou et al., 2021, Eq. 4)
 * @param room Room
 */
export function weightedAverageAlpha(room: RoomSingleFreq): number {
  const x = room[DIMENSION.x];
  const y = room[DIMENSION.y];
  const z = room[DIMENSION.z];
  return (
    (x.alpha * y.L * z.L + y.alpha * z.L * x.L + z.alpha * x.L * y.L) /
    (x.L * y.L + y.L * z.L + z.L * x.L)
  );
}

/**
 * Returns the total area of each wall in m^2.
 * @param roomSize 3D-room dimensions
 */
export function wallsTotalArea(
  roomSize: TriDimensional<number>
): SixDirectional<number> {
  const wallArea = Array(6) as SixDirectional<number>;
  wallArea[1] = wallArea[0] = chopDecimals(roomSize[1] * roomSize[2], 2);
  wallArea[3] = wallArea[2] = chopDecimals(roomSize[0] * roomSize[2], 2);
  wallArea[5] = wallArea[4] = chopDecimals(roomSize[0] * roomSize[1], 2);
  return wallArea;
}
function chopDecimals(value: number, decimals: number): number {
  return parseFloat(value.toFixed(decimals));
}

/**
 * The total surface area of the room in m^2.
 * S from (Zhou et al., 2021, Eq. 4)
 * @param Lx room length in x direction
 * @param Ly room length in y direction
 * @param Lz room length in z direction
 */
export function totalSurfaceArea(Lx: number, Ly: number, Lz: number): number {
  return 2 * (Lx * Ly + Ly * Lz + Lz * Lx);
}
export function freePathLength3D(
  Lx: number,
  Ly: number,
  Lz: number,
  S: number
) {
  return (4 * Lx * Ly * Lz) / S;
}
/**
 * Squared sound pressure of the diffuse field at t.
 * p_s^2(t) from (Zhou et al., 2021, Eq. 17)
 * This equals the Eyring formula of calculating reverberation.
 */
export function diffuseSquaredSoundPressure(
  P2_s0: number,
  c_0: number,
  L_s: number,
  m_f: number,
  alpha_s: number
): DecayFunction {
  const expFactor = (-c_0 / L_s) * (m_f * L_s - Math.log(1 - alpha_s));
  return time => P2_s0 * Math.exp(time * expFactor);
}

/**
 * Squared sound pressure at t using C.Sabine method.
 */
export function sabineSquaredSoundPressure(
  P2_s0: number,
  c_0: number,
  L_s: number,
  m_f: number,
  alpha_s: number
): DecayFunction {
  const expFactor = (-c_0 / L_s) * (m_f * L_s + alpha_s);
  return time => P2_s0 * Math.exp(time * expFactor);
}

/**
 * Initial squared sound pressure of the diffuse sound field component.
 *
 * Equals p^2_{s,1} from (Zhou et al., 2021, Eq. 6)
 *
 * Equals p^2_{s0} from (Zhou et al., 2021, Eq. 17)
 * @param room Room
 * @param direction Direction i
 */
